<template>
  <AppNavbar />
  <div class="container mt-4">
    <AppHeader
      title="Cobro de Venta"
      subtitle="Registrar cobro de la venta recién generada"
    >
      <template #buttons>
        <router-link class="btn btn-outline-secondary" :to="{ name: 'Inicio' }">
          <i class="bi bi-arrow-left me-1"></i>Volver
        </router-link>
      </template>
    </AppHeader>

    <div class="row mt-4">
      <!-- 1) Factura Seleccionada -->
      <div class="col-12 col-md-6">
        <div class="card p-3 h-100">
          <h5><i class="bi bi-check2-square me-2"></i>Factura Seleccionada</h5>
          <p class="text-muted">Factura a cobrar</p>
          <div class="list-group list-group-flush mb-3">
            <div
              v-for="inv in selectedInvoices"
              :key="inv.id"
              class="list-group-item d-flex justify-content-between align-items-center"
            >
              <div>
                <div>{{ inv.number || 'Comprobante' }}</div>
                <div><small class="text-muted">{{ inv.client || '' }}</small></div>
                <div><small class="text-muted">{{ inv.date }}</small></div>
              </div>
              <div class="fw-bold">{{ formatCurrency(inv.total) }}</div>
            </div>
          </div>
          <div class="d-flex justify-content-end fw-bold">
            Total a Cobrar:
            <span class="text-success ms-2">{{ formatCurrency(totalToCharge) }}</span>
          </div>
        </div>
      </div>

      <!-- 2) Formas de Pago -->
      <div class="col-12 col-md-6">
        <PaymentForms 
          :total="totalToCharge" 
          :types="paymentTypes"
          v-model:payments="payments" 
          cash-received-mode
          enforce-total
          @update:summary="summary = $event"
          @submit-request="submitCobro"
        />
        <div v-if="!canSubmit && !hasCredito" class="text-danger small mt-2">
          <i class="bi bi-exclamation-circle me-1"></i>{{ blockReason }}
        </div>
        <button 
          class="btn btn-dark w-100 mt-3"
          :disabled="!canSubmit"
          @click="submitCobro"
        >
          <i class="bi bi-check2-circle me-1"></i>Registrar Cobro
        </button>
      </div>
    </div>
  </div>

  <!-- COP-7A-007: seguimiento público de factura -->
  <EnviarSeguimientoModal
    :open="mostrarSeguimientoModal"
    :tracking-url="seguimientoTrackingUrl"
    :telefono-inicial="seguimientoTelefono"
    @cerrado="mostrarSeguimientoModal = false"
  />
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import AppNavbar from '@/components/AppNavbar.vue'
import AppHeader from '@/components/AppHeader.vue'
import PaymentForms from '@/components/PaymentForms.vue'
import apiService from '@/services/apiService.js'
import localPrintSettingsService from '@/services/localPrintSettingsService'
import invoiceLocalPrintService from '@/services/invoiceLocalPrintService'
import EnviarSeguimientoModal from '@/components/EnviarSeguimientoModal.vue'

const route = useRoute()

// Selected invoice passed from previous screen
const selectedInvoices = reactive([])
const payments = ref([])

// COP-7A-007: seguimiento del link de factura post-cobro. El contacto lo
// resuelve el backend (pedido > ficha del cliente > venta) y viaja en la
// respuesta del cobro — acá no hay formulario de cliente del que sacarlo.
const mostrarSeguimientoModal = ref(false)
const seguimientoTrackingUrl = ref('')
const seguimientoTelefono = ref('')
const paymentTypes = ['Efectivo', 'Tarjeta', 'Cheque', 'Transferencia', 'Credito']

// Initialize from route query ?invoice=...
function safeParseInvoice(q) {
  try {
    if (!q) return null
    const obj = typeof q === 'string' ? JSON.parse(decodeURIComponent(q)) : q
    if (!obj) return null
    return {
      // Usar movimiento_cobro_id como id principal para el cobro
      id: obj.id ?? obj.movimiento_cobro_id ?? obj.movimiento_id ?? obj.movId ?? null,
      // Preferir nro_factura si está presente
      number: obj.number ?? obj.nro_factura ?? obj.nro_comprobante ?? '',
      // Intentar obtener el nombre del cliente desde varias fuentes
  client: (obj.client ?? obj.nombre_razon_social ?? (obj.cabecera && obj.cabecera.nombre_razon_social)) || '',
      // Fecha: usar la que venga o derivar de cabecera/fecha_movimiento
      date: obj.date
        ? obj.date
        : (obj.cabecera && obj.cabecera.fecha_emision)
          ? new Date(obj.cabecera.fecha_emision).toLocaleString()
          : (obj.fecha_emision)
            ? new Date(obj.fecha_emision).toLocaleString()
            : (obj.fecha_movimiento)
              ? new Date(obj.fecha_movimiento).toLocaleString()
              : new Date().toLocaleString(),
      // Total: usar total enviado o alternativas razonables
      total: Number(obj.total ?? obj.monto_total ?? (obj.cabecera && obj.cabecera.total_iva_incluido) ?? 0)
    }
  } catch (e) {
    console.warn('No se pudo parsear la factura desde la ruta:', e)
    return null
  }
}

onMounted(() => {
  console.log('[CobroVentaRapida] route.query:', route.query)
  const inv = safeParseInvoice(route.query.invoice)
  console.log('[CobroVentaRapida] parsed invoice:', inv)
  if (inv && inv.id) {
    selectedInvoices.splice(0, selectedInvoices.length, inv)
  } else {
    alert('No se recibió una factura válida para cobrar.')
  }
})

const totalToCharge = computed(() => selectedInvoices.reduce((sum, inv) => sum + (Number(inv.total) || 0), 0))
const canAddPayment = computed(() => (payments.value || []).length > 0)

// Validación de suficiencia en el frontend, espejo del backend
// (SPEC_COBRO_PERFORMANCE_v0.1.md, RF-7).
const summary = ref(null)

const canSubmit = computed(() => {
  if (!canAddPayment.value) return false
  if (hasCredito.value) return true
  return Boolean(summary.value?.valid)
})

const blockReason = computed(() => {
  if (!canAddPayment.value) return 'Cargá con cuánto paga el cliente.'
  if (summary.value?.invalidChangeMethods?.length) {
    return `No se puede dar vuelto en ${summary.value.invalidChangeMethods.join(' / ').toLowerCase()}.`
  }
  if (!summary.value?.sufficient) {
    const falta = new Intl.NumberFormat('es-PY').format(Math.round(Number(summary.value?.remaining) || 0))
    return `Faltan ₲ ${falta} para cubrir el total.`
  }
  return ''
})

// Verificar si se seleccionó Crédito
const hasCredito = computed(() =>
  payments.value.some(p => (p.type || '').toUpperCase() === 'CREDITO')
)

function submitCobro() {
  if (!canSubmit.value) return
  if (selectedInvoices.length === 0) {
    alert('No hay factura para cobrar.')
    return
  }
  const movId = selectedInvoices[0].id
  if (!movId) {
    alert('Falta movimiento_id de la factura para registrar el cobro.')
    return
  }

  // Determinar forma de operación según si hay Crédito seleccionado
  const formaOperacion = hasCredito.value ? 'CREDITO' : 'CONTADO'

  // Si es crédito, enviar pagos vacío; si no, mapear pagos normalmente
  const pagos = hasCredito.value ? [] : (payments.value || []).map(p => {
    const metodo = (p.metodo_pago || p.metodo || p.type || p.name || '').toString().toUpperCase()
    const monto = Number(p.monto ?? p.amount ?? p.value ?? 0)
    const referencia = p.referencia_externa ?? p.referencia ?? p.reference ?? p.ref ?? null
    return {
      metodo_pago: metodo,
      monto: monto,
      referencia_externa: referencia === undefined ? null : referencia
    }
  })

  const payload = {
    movimiento_id: movId,
    forma_operacion: formaOperacion,
    pagos
  }

  console.log('Payload -> /api/cashbox/collect-sale', payload)
  apiService.post('/api/cashbox/collect-sale', payload)
    .then(async (resp) => {
      console.log('Respuesta collect-sale:', resp && resp.data)
      alert('Cobro registrado correctamente.')
      payments.value = []
      const responseData = resp?.data?.data || null
      const saleId = Number(responseData?.id_venta || 0)
      if (saleId > 0) {
        await maybePrintInvoice({
          saleId,
          movimientoId: movId,
          invoiceNumber: responseData?.nro_factura || selectedInvoices[0]?.number || '',
          fiscal: responseData?.fiscal || null
        })
      }

      // COP-7A-007: link de seguimiento (factura emitida o en
      // contingencia) — requiere PUBLIC_TRACKING_BASE_URL en el entorno.
      const trackingUrl = responseData?.tracking_url || null
      if (trackingUrl && saleId > 0) {
          seguimientoTrackingUrl.value = trackingUrl
        seguimientoTelefono.value = responseData?.tracking_contacto?.telefono || ''
          mostrarSeguimientoModal.value = true
      }
    })
    .catch(err => {
      console.error('Error enviando collect-sale:', err)
      alert('Error al registrar el cobro. Revisa la consola para más detalles.')
    })
}

async function maybePrintInvoice({ saleId, movimientoId, invoiceNumber = '', fiscal = null }) {
  const settings = localPrintSettingsService.getSettings()
  if (!settings.enabled || settings.invoicePrintMode === 'disabled') return

  if (settings.invoicePrintMode === 'confirm') {
    const confirmed = await window.confirm({
      title: 'Imprimir factura',
      message: `La factura ${invoiceNumber || saleId} está lista. ¿Deseas enviarla ahora a la impresora local?`,
      confirmText: 'Imprimir',
      cancelText: 'Omitir'
    })
    if (!confirmed) return
  }

  try {
    const result = await invoiceLocalPrintService.printInvoiceBySale({
      saleId,
      movimientoId,
      settings
    })
    if (result?.mode === 'offline_contingency' || fiscal?.status === 'OFFLINE_PENDING_SYNC') {
      alert.success('Comprobante temporal enviado al agente local. Queda pendiente de validación fiscal.')
      return
    }
    alert.success('Factura enviada al cliente local de impresión.')
  } catch (error) {
    console.error('No se pudo imprimir la factura localmente', error)
    alert.error(error?.message || 'No se pudo imprimir la factura en el cliente local.')
  }
}

function formatCurrency(value) {
  return new Intl.NumberFormat('es-PY', {
    style: 'currency',
    currency: 'PYG'
  }).format(value)
}

// No precargar pagos automáticamente - el usuario debe seleccionar manualmente
</script>

<style scoped>
.list-group {
  max-height: 400px;
  overflow-y: auto;
}
</style>
